// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import ProgressBar from './ProgressBar';
import './GateWarpEffect.css';

/**
 * GateWarpEffect - Dimensional portal warp effect for gate travel
 * 
 * Creates a dramatic vortex/tunnel effect with magenta colors
 * for traveling through warp gates between universes
 */
const GateWarpEffect = ({ duration = 5000, destinationUniverseId = null }) => {
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / duration) * 100, 100);
      setProgress(newProgress);
      
      if (newProgress >= 100) {
        clearInterval(interval);
      }
    }, 50);
    
    return () => clearInterval(interval);
  }, [duration]);
  
  // Generate rotating rings
  const rings = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    delay: i * 0.1,
    scale: 1 + (i * 0.15)
  }));
  
  // Generate energy particles
  const particles = Array.from({ length: 40 }, (_, i) => ({
    id: i,
    angle: (i / 40) * 360,
    delay: i * 0.05,
    distance: 20 + (i % 4) * 10
  }));
  
  return createPortal(
    <div className="gate-warp-effect">
      {/* Vortex tunnel */}
      <div className="gate-vortex-tunnel">
        {rings.map(ring => (
          <div
            key={ring.id}
            className="gate-vortex-ring"
            style={{
              animationDelay: `${ring.delay}s`,
              transform: `scale(${ring.scale})`
            }}
          />
        ))}
      </div>
      
      {/* Energy particles spiraling */}
      <div className="gate-energy-particles">
        {particles.map(particle => (
          <div
            key={particle.id}
            className="gate-particle"
            style={{
              '--angle': `${particle.angle}deg`,
              '--distance': `${particle.distance}px`,
              animationDelay: `${particle.delay}s`
            }}
          />
        ))}
      </div>
      
      {/* Center portal */}
      <div className="gate-portal-center">
        <div className="gate-portal-core" />
      </div>
      
      {/* Universe destination splash - fades in at 50% progress */}
      {destinationUniverseId && progress > 50 && (
        <div
          className="gate-universe-splash"
          style={{
            backgroundImage: `url(/images/universes/${destinationUniverseId}.png)`,
            opacity: Math.min((progress - 50) / 30, 0.7),
          }}
        />
      )}

      {/* Progress indicator */}
      <div className="gate-travel-progress">
        <div className="gate-progress-label">DIMENSIONAL TRANSIT</div>
        <ProgressBar 
          value={progress}
          max={100}
          color="secondary"
          showLabel={true}
          animated={true}
          striped={true}
          size="medium"
        />
      </div>
      
      {/* Overlay flash at start and end */}
      <div className={`gate-flash ${progress < 10 ? 'gate-flash-in' : ''} ${progress > 90 ? 'gate-flash-out' : ''}`} />
    </div>,
    document.body
  );
};

export default GateWarpEffect;
